import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ContentChild, TemplateRef, ViewChild } from "@angular/core";
import { Subject, Observable, of } from "rxjs";
import { distinctUntilChanged, debounceTime, switchMap, takeUntil, tap } from "rxjs/operators";
import { AutcompleteOptionTemplateDirective, AutcompleteLabelTemplateDirective, AutocompleteHeaderTemplateDirective, AutocompleteFooterTemplateDirective } from "../helpers/autocomplete-templates.directive";
import { NgSelectComponent, NgLabelTemplateDirective, NgOptionTemplateDirective, NgHeaderTemplateDirective, NgFooterTemplateDirective } from "@ng-select/ng-select";
import { FormsModule } from "@angular/forms";
import { NgTemplateOutlet, AsyncPipe } from "@angular/common";
import { HighlightSearchDirective } from "../helpers/highlight-search.directive";
import { InternalLabelPipe } from "../helpers/internal-label.pipe";

@Component({
   selector: "my-cloud-search-autocomplete",
   templateUrl: "./search-autocomplete.component.html",
   styleUrls: ["./search-autocomplete.component.scss"],
   imports: [
      NgSelectComponent,
      FormsModule,
      NgLabelTemplateDirective,
      NgTemplateOutlet,
      NgOptionTemplateDirective,
      HighlightSearchDirective,
      NgHeaderTemplateDirective,
      NgFooterTemplateDirective,
      AsyncPipe,
      InternalLabelPipe
   ]
})
export class SearchAutocompleteComponent<T> implements OnInit, OnDestroy {
   private destroyed = new Subject<void>();
   private initialSearchDone = false;
   private modelSearchSubject: Subject<string> = new Subject<string>();

   public modelObservable!: Observable<Array<T>>;
   public loading = false;

   public readonly customSearchFunction = (): boolean => true;

   public currentSearchTerm: string = "";

   public readonly closeSelect = (): void => this.ngSelect?.close();

   @Input() public searchFn: (
      searchTerm: string
   ) => Observable<Array<T>> = () => of([]);

   @Input() public placeholder: string = "";
   @Input() public typeForSuggestionsLabel: string =
      "Start typing for more suggestions...";
   @Input() public defaultLabel?: keyof T | "string" = "string";

   @Input() public disabled = false;
   @Input() public required = false;
   @Input() public multiple = false;
   @Input() public showLoader = true;
   @Input() public highlightSearch = true;
   @Input() public performInitialSearchOnFocus = true;
   @Input() public clearable = true;
   @Input() public forceSearchOnOpen = false;
   @Input() public distinctUntilChanged = true;

   @Input() public selectedModel: T | undefined;
   @Input() public selectedModels: Array<T> = [];

   @Output() public selectedModelChange = new EventEmitter<T | undefined>();
   @Output() public selectedModelsChange = new EventEmitter<Array<T>>();

   @ViewChild(NgSelectComponent) private ngSelect: NgSelectComponent;

   @ContentChild(AutcompleteOptionTemplateDirective, { read: TemplateRef })
   public optionTemplate?: TemplateRef<any>;
   @ContentChild(AutcompleteLabelTemplateDirective, { read: TemplateRef })
   public labelTemplate?: TemplateRef<any>;
   @ContentChild(AutocompleteHeaderTemplateDirective, { read: TemplateRef })
   public headerTemplate?: TemplateRef<any>;
   @ContentChild(AutocompleteFooterTemplateDirective, { read: TemplateRef })
   public footerTemplate?: TemplateRef<any>;

   public ngOnInit(): void {
      this.modelObservable = this.modelSearchSubject.pipe(
         takeUntil(this.destroyed),
         distinctUntilChanged((prev, curr) =>
            this.distinctUntilChanged ? prev === curr : false
         ),
         debounceTime(500),
         switchMap(searchTerm => {
            this.loading = true;
            return this.searchFn(searchTerm);
         }),
         tap(_ => {
            this.loading = false;
         })
      );
   }

   public ngOnDestroy(): void {
      this.destroyed.next();
      this.destroyed.complete();
   }

   public get getSelectedModel(): T | Array<T> | undefined {
      if (this.multiple) {
         return this.selectedModels;
      }

      return this.selectedModel;
   }

   public onSearchModels(event: any): void {
      this.currentSearchTerm = event.term;
      this.modelSearchSubject.next(event.term);
   }

   public onSelectedModelChanged(model?: T | Array<T>): void {
      if (Array.isArray(model)) {
         this.selectedModels = model;
         return this.selectedModelsChange.emit(model);
      }

      this.selectedModel = model;
      return this.selectedModelChange.emit(model);
   }

   public doInitialSearch(): void {
      if (!this.performInitialSearchOnFocus || this.initialSearchDone) {
         return;
      }

      this.initialSearchDone = true;
      return this.modelSearchSubject.next("");
   }

   public forceSearch(): void {
      if (
         !this.initialSearchDone ||
         !this.forceSearchOnOpen ||
         this.distinctUntilChanged
      ) {
         return;
      }

      return this.modelSearchSubject.next("");
   }
}
